import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePermissionGuardRedirect } from "../../../hooks/usePermissionGuard";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { RenderProjectStatus } from "../../../utils/CommonStatusItems";
import { useSelector } from "react-redux";
import {
    GetPCProjectList,
    GetPCProjectListFilters,
    DeletePCProject
} from "../../../services/ProjectCenterServices";
import PermissionsGate from "../../../utils/permissionGate";
import DataTableComponentBackendPagination from "../../common/DataTableComponentBackendPagination";
import Swal from "sweetalert2";
import {processMilestoneData, formatDate} from "../utils/ProjectCenterUtils";
import {TABLE_DATE_FORMAT} from "../../../utils/Constants";
import moment from "moment";
import styled from "styled-components";
const statusBadgeClass = {
    "Active": "badge-soft-success",
    "On Hold": "badge-soft-warning",
    "Complete": "badge-soft-info",
    "Cancelled": "badge-soft-secondary",
};

const FilterButtonWrapper = styled.div`
    flex-direction: row !important;
`
const getMilestoneData = (row) => {
    if (!row._milestoneData) {
        row._milestoneData = processMilestoneData(row.milestones, row.start_date || row.created_at);
    }
    return row._milestoneData;
};
const PCProjectList = () => {
    usePermissionGuardRedirect(["projectcenterprojectsview"]);
    const givenPermissions = useSelector((state) => state.givenPermission);


    const hasNonWatcherPermission = () => {
        const permissions = [];
        Object.keys(givenPermissions).forEach((key) => {
            permissions.push(givenPermissions[key]);
        });
        return permissions.includes("projectcenternowatchview");
    };
    const [watchingOnly, setWatchingOnly] = useState(hasNonWatcherPermission);
    const handleDeleteProject = async (id) => {
        try {
            // confirmation swal
            Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
            }).then((swalResult) => {
                if (swalResult.isConfirmed) {
                    const deleteReponse = DeletePCProject(id).then((deleteResult) => {
                        if (deleteResult.status === 200) {
                            window.location.reload();
                        }
                    });
                }
            });
        } catch (error) {
            console.error(error);
        }
    }
    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            sortField: "name",
            sortable: true,
            cell: (row) => (
                <Link
                    to={`/project-center/projects/open/${row.id}`}
                    className="as-text-blue curser feature-name"
                    title={row.name}
                >
                    {row?.name?.trim() ? (
                        <span className="textLimit100">{row.name}</span>
                    ) : (
                        "-"
                    )}
                </Link>
            ),
        },
        {
            name: "Department",
            selector: "department_name",
            sortField: "department",
            sortable: true,
            cell: (row) => (
                <div className="ticket-id">
                    {row.department_name ? (
                        <span title={row.department_name} className="overflow-ellipsis2">
                            {row.department_name}
                        </span>
                    ) : (
                        "-"
                    )}
                </div>
            ),
        },
        {
            name: "Template",
            selector: "template_name",
            sortField: "template",
            sortable: true,
            cell: (row) => (
                <div className="ticket-id">
                    {row.template_name ? (
                        <span title={row.template_name} className="overflow-ellipsis2">
                            {row.template_name}
                        </span>
                    ) : (
                        "-"
                    )}
                </div>
            ),
        },
        {
            name: "Status",
            selector: "status",
            sortField: "status",
            sortable: true,
            cell: (row) => (
                <span className={`badge ${statusBadgeClass[row.status] || "badge-soft-secondary"}`}>
                    {RenderProjectStatus(row.status || "")?.html || "-"}
                </span>
            ),
        },
        {
            name: "Completion %",
            selector: "completion",
            sortField: "completion",
            sortable: true,
            cell: (row) => (
                <span>{getMilestoneData(row).completion ? getMilestoneData(row).completion : "0%"}</span>
            )
        },
        {
            name: "Next Milestone",
            selector: "next_deliverable",
            sortField: "next_deliverable",
            cell: (row) => (
                <span>{getMilestoneData(row).nextDeliverable ? getMilestoneData(row).nextDeliverable : "-"}</span>
            )
        },
        {
            name: "Next Milestone Date",
            selector: "next_deliverable_date",
            sortField: "next_deliverable_date",
            sortable: true,
            cell: (row) => (
                <span className={moment(getMilestoneData(row).nextDeliverableDate).unix() < moment().unix() ? "colorRed" : ""}>{getMilestoneData(row).nextDeliverableDate ? moment(getMilestoneData(row).nextDeliverableDate).format(TABLE_DATE_FORMAT) : "-"}</span>
            )
        },
        {
            name: "Duration",
            selector: "estimate_duration",
            sortField: "estimate_duration",
            cell: (row) => (
                <>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Expected</small>
                            <p className={`mt-1 ${getMilestoneData(row).DurationExpected?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).DurationExpected ? getMilestoneData(row).DurationExpected : "-"}
                            </p>
                        </div>
                    </span>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Cardinal</small>
                            <p className={`mt-1 ${getMilestoneData(row).DurationCardinal?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).DurationCardinal ? getMilestoneData(row).DurationCardinal : "-"}
                            </p>
                        </div>
                    </span>
                </>
            )
        },
        {
            name: "Remaining",
            selector: "estimate_time_left",
            sortField: "estimate_time_left",
            cell: (row) => (
                <>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Expected</small>
                            <p className={`mt-1 ${getMilestoneData(row).TimeLeftExpected?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).TimeLeftExpected}
                            </p>
                        </div>
                    </span>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Cardinal</small>
                            <p className={`mt-1 ${getMilestoneData(row).TimeLeftCardinal?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).TimeLeftCardinal}
                            </p>
                        </div>
                    </span>
                </>
            )
        },
        {
            name: "End Date",
            selector: "estimate_end_date",
            sortField: "estimate_end_date",
            cell: (row) => (
                <>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Expected</small>
                            <p className={`mt-1 ${getMilestoneData(row).EndDateExpected?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).EndDateExpected !== "-" ? moment(getMilestoneData(row).EndDateExpected).format(TABLE_DATE_FORMAT): "-"}
                            </p>
                        </div>
                    </span>
                    <span>
                        <div className="d-flex flex-column">
                            <small className="text-muted-assessment"><i className="fal fa-exclamation-triangle"></i> Cardinal</small>
                            <p className={`mt-1 ${getMilestoneData(row).EndDateCardinal?.startsWith("-") ? "colorRed" : ""}`}>
                                {getMilestoneData(row).EndDateCardinal !== "-" ? moment(getMilestoneData(row).EndDateCardinal).format(TABLE_DATE_FORMAT): "-"}
                            </p>
                        </div>
                    </span>
                </>
            )
        },
        {
            name: "Last Updated",
            selector: "updated_at",
            sortField: "updated_at",
            sortable: true,
            cell: (row) => <span>{moment(row.updated_at).format(TABLE_DATE_FORMAT)}</span>,
        },
        {
            name: "Actions",
            selector: "",
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons d-flex">
                        <Link
                            to={`/project-center/projects/open/${row.id}`}
                            className="btn btn-primary rounded-circle"
                            title="Open"
                        >
                            <i className="fal fa-folder-open"></i>
                        </Link>
                        <PermissionsGate scopes={['projectcenterprojectsdelete']} errorProps={{ disabled: true }}>
                            <button
                                className="btn btn-danger rounded-circle"
                                title="Delete"
                                onClick={() => handleDeleteProject(row.id)}
                            >
                                <i className="fal fa-trash-alt"></i>
                            </button>
                        </PermissionsGate>
                    </div>
                </div>
            ),
        },
    ], []);

    const filterConfig = [
        {
            key: "viaDepartment",
            label: " Department",
            optionsKey: "DepartmentFilter",
        },
        {
            key: "viaStatus",
            label: " Status",
            optionsKey: "StatusFilter",
            renderFunction: (value) => RenderProjectStatus(value),
        },
        {
            key: "viaTemplate",
            label: " Template",
            optionsKey: "TemplateFilter",
        },
        {
            key: "viaAccountable",
            label: " Accountable",
            isSearchFilter: true,
            optionsKey: "UserFilter",
        },
        {
            key: "viaResponsible",
            label: " Responsible",
            isSearchFilter: true,
            optionsKey: "UserFilter",
        }
    ];

    return (
        <div
            className="tab-pane fade active show"
            id="pills-tab11"
            role="tabpanel"
            aria-labelledby="pills-tab1"
        >
            <DataTableComponentBackendPagination
                columns={columns}
                getDataApi={GetPCProjectList}
                getFiltersApi={GetPCProjectListFilters}
                fileName="Project_List"
                filterConfig={filterConfig}
                showExport={false}
                storeFilters={true}
                storageKey="PCProjectList"
                showFilter={true}
                showSearch={true}
                defaultSortField="name"
                defaultSortAsc={true}
                additionalParams={{
                    isWatcher: watchingOnly,
                    forcedWatcher: !hasNonWatcherPermission(),
                }}
                LoadingComponent={<SkeletonTicketList />}
                tableButton={[
                    <div className="search-filter-div-right">
                        <FilterButtonWrapper className=" filter-search-bar-blk">
                    <PermissionsGate 
                    scopes={["projectcenternowatchview"]}
                    errorProps={{ disabled: true }} >
                    <button
                        type="button"
                        className={`btn btn-primary mr-2`}
                        onClick={() => setWatchingOnly(prev => !prev)}
                        title={!hasNonWatcherPermission() 
                            ? "You do not have permissions to view projects which you are not responsible, accountable, the creator of or an assigned watcher." 
                            : "Projects you are watching, responsible for or accountable for"}
                    >
                        <i className="fal fa-eye"></i>{" "}
                        {watchingOnly || !hasNonWatcherPermission() ? "Watching Only" : "All Projects"}
                    </button>
                    </PermissionsGate>
                    <Link to={`/project-center/projects/open`}>
                        <PermissionsGate
                            scopes={["projectcenterprojectsadd"]}
                            errorProps={{ disabled: true }}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                title="Create new"
                            >
                                <i className="fal fa-plus"></i>Create New
                            </button>
                        </PermissionsGate>
                    </Link>
                    </FilterButtonWrapper>
                    </div>,
                ]}
            />
        </div>
    );
};

export default PCProjectList;